import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import TimelinePhotoIcon from '../../svgs/TimelinePhotoIcon';
import {
  CAPTION_LINE_DIVIDER_COLOR,
  CAPTION_LINE_FONT_SIZE,
  CAPTION_LINE_LINE_HEIGHT,
  CAPTION_LINE_VERTICAL_PADDING,
  CaptionCursor,
} from './captionLineStyles';

import type { TranscriptElementWithViewData } from '../../types.ts/transcript';

type ElementProps = {
  value: string | React.JSX.Element | null;
  tokenId: string | undefined; // punct is undefined
  lineId: string;
  index: number;
  isHighlighted: boolean;
  isHidden: boolean;
  isPhotoHighlight: boolean;
  karaokeBreak?: boolean;
};

const Element = React.memo((props: ElementProps) => {
  const {
    value,
    tokenId,
    lineId,
    index,
    isHighlighted,
    isHidden,
    isPhotoHighlight,
    karaokeBreak,
  } = props;
  const valueNode = value === '\n' ? '' : value;

  return (
    <Word
      key={`element-${tokenId}`}
      data-index={index}
      data-token-id={tokenId}
      data-line-id={lineId}
      isHighlighted={isHighlighted}
      isHidden={isHidden}
      isPhotoHighlight={isPhotoHighlight}
      karaokeBreak={karaokeBreak}
      preWrapWhitespace={typeof value === 'string' && value.trim() === ''}
    >
      {valueNode}
    </Word>
  );
});

type Props = {
  cursor: { from: number; to: number };
  transcriptElements: TranscriptElementWithViewData[];
  isPlaying: boolean;
  setActiveElement: (elementId: string) => void;
};

export const KaraokeLinesView: React.FC<Props> = observer(
  ({ cursor, transcriptElements, isPlaying, setActiveElement }: Props) => {
    // Group consecutive transcript elements by viewData.lineId so each
    // caption row renders as its own <Line> block — same DOM shape as
    // TranslatedCaptionsView. Elements with no viewData are skipped.
    type GroupedItem = {
      token: TranscriptElementWithViewData;
      index: number;
      viewData: NonNullable<TranscriptElementWithViewData['viewData']>;
    };
    const lines = React.useMemo(() => {
      const groups: Array<{ lineId: string; items: GroupedItem[] }> = [];
      transcriptElements.forEach((token, index) => {
        if (!token.viewData) return;
        const { lineId } = token.viewData;
        const item: GroupedItem = { token, index, viewData: token.viewData };
        const last = groups[groups.length - 1];
        if (!last || last.lineId !== lineId) {
          groups.push({ lineId, items: [item] });
        } else {
          last.items.push(item);
        }
      });
      return groups;
    }, [transcriptElements]);

    return (
      <Container>
        {lines.map(line => (
          <Line key={line.lineId} data-line-id={line.lineId}>
            {line.items.map(({ token, index, viewData }) => {
              const {
                isHidden,
                isLastInLine,
                lineId,
                punchListItem,
                lastPhotoHighlightElementId,
                lastPhotoHighlightActive,
              } = viewData;

              return (
                <React.Fragment key={token.id ?? `idx-${index}`}>
                  {!isPlaying && index === cursor.from ? (
                    <CaptionCursor data-index={cursor.from} />
                  ) : null}
                  <Element
                    karaokeBreak={isLastInLine}
                    value={token.value}
                    tokenId={token.id}
                    lineId={lineId}
                    index={index}
                    isHighlighted={Boolean(
                      isPlaying && index >= cursor.from && index < cursor.to,
                    )}
                    isHidden={isHidden}
                    isPhotoHighlight={!!punchListItem}
                  />
                  {/* PLAT-11914: a soft line break, inside the same row. */}
                  {viewData.softBreakAfter ? <br /> : null}
                  {!isPlaying &&
                  index === transcriptElements.length - 1 &&
                  index + 1 === cursor.from ? (
                    <CaptionCursor data-index={cursor.from} />
                  ) : null}
                  {lastPhotoHighlightElementId ? (
                    <HighlightImageWrapper>
                      <HighlightImage
                        isAssigned={lastPhotoHighlightActive}
                        key={`highlight-${index}`}
                        onClick={() => {
                          setActiveElement(lastPhotoHighlightElementId);
                        }}
                      >
                        <TimelinePhotoIcon
                          width="10"
                          height="10"
                          strokeColor={
                            lastPhotoHighlightActive
                              ? 'var(--foreground)'
                              : 'var(--text-secondary)'
                          }
                        ></TimelinePhotoIcon>
                      </HighlightImage>
                    </HighlightImageWrapper>
                  ) : null}
                </React.Fragment>
              );
            })}
          </Line>
        ))}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${CAPTION_LINE_FONT_SIZE};
  line-height: ${CAPTION_LINE_LINE_HEIGHT};
`;

const Line = styled.div`
  padding: ${CAPTION_LINE_VERTICAL_PADDING} 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${CAPTION_LINE_DIVIDER_COLOR};
  }
`;

const colorByState = (
  isHighlighted: boolean,
  isHidden: boolean,
  isPhotoHighlight: boolean,
) => {
  if (isHighlighted) return 'black';
  if (isPhotoHighlight) return 'var(--primary-hover)';
  if (isHidden) return '#F69B12'; // Hidden tokens styled similarly to cut tokens
  return 'var(--text-secondary)'; // Default color
};

export const Word = styled.span.attrs(
  (props: {
    isHighlighted: boolean;
    karaokeBreak?: boolean;
    isHidden: boolean;
    isPhotoHighlight: boolean;
    preWrapWhitespace?: boolean;
  }) => props,
)`
  color: ${props =>
    colorByState(props.isHighlighted, props.isHidden, props.isPhotoHighlight)};
  background-color: ${props => (props.isHighlighted ? '#dfb615' : 'none')};
  cursor: pointer;
  text-decoration: ${props => (props.isPhotoHighlight ? 'underline' : 'none')};
  opacity: ${props => (props.isHidden ? 0.9 : 1)};

  ${props => props.preWrapWhitespace && `white-space: pre-wrap;`}

  &:hover {
    color: lightgreen;
  }
`;

export const HighlightImageWrapper = styled.div`
  position: relative;
  display: inline;
`;

export const HighlightImage = styled.div.attrs(
  (props: { isAssigned: boolean }) => props,
)`
  background: ${props =>
      props.isAssigned
        ? 'linear-gradient(0deg, var(--primary-hover) 0%, var(--primary-hover) 100%), var(--primary-hover)'
        : 'linear-gradient(0deg, #4f4f4f 0%, #4f4f4f 100%), lightgray'}
    50% / cover no-repeat;
  color: #4f4f4f;
  border-radius: 50%;
  padding: 6px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;
